import { useMemo } from 'react'

import { IconTrash } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'

import { PropertyType } from 'apiTypes/PropertyType'

import { ButtonIcon } from 'components/Buttons'
import { Input, Select } from 'components/FormFields'

import { Header } from './Header'

type Props = {
	index: number
	isReadOnly: boolean
	onRemove: () => void
}

export const PropertyForm = ({ index, isReadOnly, onRemove }: Props) => {
	const { formatMessage } = useIntl()
	const propertyTypeOptions = useMemo(
		() => [
			{
				value: PropertyType.House,
				label: formatMessage({ id: 'flow.assets.propertyType.house', defaultMessage: 'House' }),
			},
			{
				value: PropertyType.Appartment,
				label: formatMessage({ id: 'flow.assets.propertyType.appartment', defaultMessage: 'Appartment' }),
			},
			{
				value: PropertyType.HolidayHome,
				label: formatMessage({ id: 'flow.assets.propertyType.holidayHome', defaultMessage: 'Holiday home' }),
			},
			{
				value: PropertyType.ParentPurchase,
				label: formatMessage({ id: 'flow.assets.propertyType.parentPurchase', defaultMessage: 'Parent purchase' }),
			},
			{
				value: PropertyType.DecommissionedFarm,
				label: formatMessage({
					id: 'flow.assets.propertyType.decommissionedFarm',
					defaultMessage: 'Decommissioned farm',
				}),
			},
			{
				value: PropertyType.LivingAtHome,
				label: formatMessage({ id: 'flow.assets.propertyType.livingAtHome', defaultMessage: 'Living at home' }),
			},
			{
				value: PropertyType.Rental,
				label: formatMessage({ id: 'flow.assets.propertyType.rental', defaultMessage: 'Rental' }),
			},
		],
		[formatMessage],
	)

	return (
		<section>
			<Header>
				<h2>
					<FormattedMessage
						id='flow.assets.propertyTitle'
						defaultMessage='Asset {index}: Property'
						values={{ index: index + 1 }}
					/>
				</h2>
				{!isReadOnly && (
					<ButtonIcon
						aria-label={formatMessage({
							id: 'flow.assets.buttonRemoveProperty',
							defaultMessage: 'Remove property',
						})}
						onClick={onRemove}
						size={22}
						renderIcon={(props) => <IconTrash {...props} strokeWidth={1.8} />}
					/>
				)}
			</Header>
			<Select
				name={`assets.${index}.propertyType`}
				label={<FormattedMessage id='flow.assets.labelPropertyType' defaultMessage='Property type' />}
				options={propertyTypeOptions}
				isReadOnly={isReadOnly}
				isClearable={false}
				isRequired
			/>
			<Input
				name={`assets.${index}.address`}
				label={<FormattedMessage id='flow.assets.labelAddress' defaultMessage='Address' />}
				isReadOnly={isReadOnly}
				isRequired
			/>
			<Input
				name={`assets.${index}.comment`}
				label={<FormattedMessage id='flow.assets.labelComment' defaultMessage='Comment' />}
				type='multiline'
			/>
		</section>
	)
}
